import { StrictMode, lazy } from 'react';

import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import ReactDOM from 'react-dom/client';

import { version } from '../package.json';

import { preloadLobbyBanner } from './components/banners/headers/preloadLobbyBanner';
import { DOCUMENT_TITLE } from './config/constants';
import type { CleanGeoData, RawGeoData } from './features/geography/types';
import { resolveAvailableLanguages } from './i18n/availableLanguages';
import i18n, { initI18Next } from './i18n/config';
import { getUserLanguage } from './i18n/helpers';
import { analytics } from './services/analytics';
import { auth, type Profile } from './services/auth';
import { handleAffiliateUTM, sendGoogleTagUTMSource } from './services/googleTagEvent';
import { intercom } from './services/intercom';
import { createLDContext, getBootstrapFlags, setServerFlags } from './services/launchDarkly';
import { fetchAnonymousBootstrapFlags } from './services/launchDarklyApi';
import { lifecycleEventEmitterService } from './services/lifecycleEventEmitter/LifecycleEventEmitterService';
import { warmupPaycomScript } from './services/paycomPreload';
import { platformContextService } from './services/platformContext/PlatformContextService';
import { primeBanners } from './services/ui';
import { generalStore } from './stores/generalStore';
import { liveGeoStore } from './stores/live-geo/LiveGeoStore';
import { isCmsPages } from './utils/isExemptPage';
import { devError } from './utils/log';

const AppError = lazy(() => import('./pages/errors/AppError'));

export async function render(component: React.ComponentType) {
  document.title = DOCUMENT_TITLE;
  platformContextService.initFromURL();
  lifecycleEventEmitterService.emitMessage('ON_APP_STARTED');
  const el = document.getElementById('root');

  if (!el) {
    throw new Error('Root element not found');
  }

  handleAffiliateUTM();
  sendGoogleTagUTMSource();

  const root = ReactDOM.createRoot(el);
  generalStore.appBootParams = Object.fromEntries(new URLSearchParams(window.location.search).entries());

  let ComponentToRender = component;
  let profile: Profile | null = null;

  try {
    if (window.newrelic) {
      window.newrelic.setApplicationVersion(version);
    }

    // We don't call login logic for cms pages, we don't want to limit access by geoblock to pages such as privacy policy, terms of use, etc...
    if (!isCmsPages()) {
      profile = await auth.initAuth();
    }
  } catch (e: any) {
    devError('Error initializing app', e);

    if (e.response?.status !== 403) {
      ComponentToRender = function AppErrorWrapper() {
        return <AppError error={e as Error} />;
      };
    }
  } finally {
    console.log(`Version ${version} 🎉`);

    let currentGeo: { clean: CleanGeoData | null; raw: RawGeoData | null } | null = null;
    let anonymousBootstrapFlags: Record<string, any> | null = null;

    if (!profile) {
      [currentGeo, anonymousBootstrapFlags] = await Promise.all([
        liveGeoStore.getCurrentGeoData(),
        fetchAnonymousBootstrapFlags(),
      ]);
    }

    // Resolved before i18n so language detection can be gated by the flag. The bootstrap payload is
    // already in hand at this point — backend-evaluated at login for authenticated users, fetched
    // just above for anonymous ones — which is what makes this possible: the LD client itself is
    // not initialised until asyncWithLDProvider further down, long after the language is chosen.
    const bootstrapFlags = getBootstrapFlags(profile?.ldBootstrapFlags, anonymousBootstrapFlags);
    // Seed before the first paint; UserStore.setUserProfile keeps it fresh from here on.
    setServerFlags(profile);
    const availableLanguages = resolveAvailableLanguages(bootstrapFlags?.available_languages);

    // Awaited rather than fire-and-forget so the detected language is settled before priming the
    // banner request below — getUserLanguage() falls back to i18n.language. i18next initialises
    // from statically bundled resources and browser-local detectors, so this is a microtask, not
    // a network round trip.
    await initI18Next(currentGeo?.clean?.defaultLanguage, availableLanguages);

    if (window.newrelic) {
      const languageDiagnostics = {
        languageRequested: profile?.language?.toUpperCase() ?? i18n.language,
        languageResolved: getUserLanguage(),
        availableLanguagesResolved: availableLanguages.join(','),
      };

      Object.entries(languageDiagnostics).forEach(([key, value]) => {
        window.newrelic.setCustomAttribute(key, value);
      });
      window.newrelic.addPageAction('LanguageResolved', languageDiagnostics);
    }

    // Start the lobby banner request now: the profile (logged in) or request-IP geo (logged out)
    // that the response varies on is already resolved, but UiStore would not issue this until
    // after asyncWithLDProvider and root.render(). The banner <img> is the LCP element for most
    // sessions, so that delay lands directly on LCP. UiStore adopts this request when it starts.
    const primedBannersRequest = isCmsPages() ? null : primeBanners(getUserLanguage());

    const userLdContext = createLDContext(profile, currentGeo?.raw);

    // Warm the banner image the moment its URL is known. Without this the image cannot start
    // downloading until React has rendered the <img>, i.e. after asyncWithLDProvider and
    // root.render() — the preload link lets it download alongside them instead. Never awaited:
    // a slow or failed banner request must not hold up the app.
    if (primedBannersRequest) {
      // Same flag and default GenericHeader resolves via resolveFlag(), read from the bootstrap
      // payload because the LD client is not initialised yet at this point.
      const animationFlag = bootstrapFlags?.enable_banner_rive_animation;
      void primedBannersRequest
        .then((response) => preloadLobbyBanner(response.data.banners, animationFlag !== false))
        .catch(() => undefined);
    }

    const initDeferredServices = () => {
      analytics.init();
      intercom.init(profile);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(initDeferredServices);
    } else {
      // Safari doesn't support requestIdleCallback — wait for the first
      // frame to paint, then yield so the paint completes before we init.
      requestAnimationFrame(() => setTimeout(initDeferredServices, 0));
    }

    window.addEventListener('beforeunload', function userLeaving() {
      void analytics.sendEvent('app_close', {} as never);
    });

    if (profile) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => warmupPaycomScript());
      } else {
        setTimeout(() => warmupPaycomScript(), 0);
      }
    }

    // https://docs.launchdarkly.com/sdk/client-side/react/react-web/#configuring-the-react-sdk
    // https://docs.launchdarkly.com/sdk/features/bootstrapping
    const LDProvider = await asyncWithLDProvider({
      clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
      context: userLdContext,
      timeout: bootstrapFlags ? 1 : 3,
      options: {
        fetchGoals: false,
        flushInterval: 60000, // one minute (default is 2 sec)
        bootstrap: bootstrapFlags, // If undefined, LD will fetch flags normally (graceful fallback)
        // Send the context in the request body instead of base64-encoded in the URL. Large contexts
        // (high-tier players accumulate big user-stats blobs) otherwise overflow the URL and the
        // flag-settings request fails with a 500, dropping the context to just `key`.
        useReport: true,
      },
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
    });
    root.render(
      <StrictMode>
        <LDProvider>
          <ComponentToRender />
        </LDProvider>
      </StrictMode>,
    );
  }
}
